<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import { toLowerCase } from "remeda";
import {
  type ControlaAppOperatingSystem,
  controlaAppOperatingSystemToKnownOs,
} from "shared-core/types/agent-download.ts";
import { isMobileOperatingSystem } from "shared-core/types/operating-system.ts";
import { useApi } from "shared-ui/utils/api";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import AppInstallSteps from "@/components/app-setup/AppInstallSteps.vue";
import { useActivelyUsingAgentPoll } from "@/composables/use-actively-using-agent-poll.ts";
import RegistrationLayout from "@/layouts/RegistrationLayout.vue";
import { finishDeviceSetup } from "@/utils/device-setup.ts";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const { t } = useI18n();
const route = useRoute("/setup-device/[os]");
const router = useRouter();
const api = useApi();

const os = computed<ControlaAppOperatingSystem>(
  () => route.params.os as ControlaAppOperatingSystem,
);
const retryTimeout: number =
  (import.meta.env.VITE_RETRY_TIMEOUT_MULTIPLIER ?? 1) * 1000;

useSeoMeta({
  title: () => t("onboarding.install_app.title"),
});

useActivelyUsingAgentPoll(api, retryTimeout, async () => {
  await finishDeviceSetup(router);
  return true;
});
</script>

<template>
  <RegistrationLayout>
    <template #step-content>
      <div class="mb-12 flex w-full flex-col items-center gap-4 md:gap-6">
        <h1 class="text-5xl! font-medium!">
          {{ t("onboarding.install_app.a_last_step") }}
        </h1>
        <p>
          {{
            t("onboarding.install_app.continue_by_installing_app", {
              deviceType: toLowerCase(
                isMobileOperatingSystem(controlaAppOperatingSystemToKnownOs(os))
                  ? t("admin_add_device.type_mobile")
                  : t("admin_add_device.type_computer"),
              ),
            })
          }}
        </p>
        <AppInstallSteps :os="os" />
      </div>
    </template>
  </RegistrationLayout>
</template>
